import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  InputAdornment,
  List,
  Paper,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Button,
  Collapse
} from '@mui/material';
import { 
  Search as SearchIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { Vehicle } from '../../types';
import { vehicleService, maintenanceService, maintenanceSettingsService } from '../../services/api';
import { getStatusLabel, getStatusColor } from '../../utils/vehicleUtils';
import { getDropdownMenuProps } from '../../utils/dropdownProps';
import { SYSTEM_MAINTENANCE_TYPES } from '../../constants/maintenanceTypes';

const VehicleList: React.FC = () => {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [parkingFilter, setParkingFilter] = useState<string>('');
  const [maintenanceTypeFilter, setMaintenanceTypeFilter] = useState<string>('');
  const [maintenanceStatusFilter, setMaintenanceStatusFilter] = useState<string>('');
  const [dateFilter, setDateFilter] = useState<string>('');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [maintenanceTypes, setMaintenanceTypes] = useState<any[]>([]);

  useEffect(() => {
    loadVehicles();
    loadMaintenanceTypes();
  }, []);

  const loadVehicles = async () => {
    try {
      const response = await vehicleService.getAll();
      setVehicles(response.data.data);
    } catch (err) {
      console.error('Error loading vehicles:', err);
      setError('Erreur lors du chargement des véhicules');
    } finally {
      setLoading(false);
    }
  };

  const loadMaintenanceTypes = async () => {
    try {
      const response = await maintenanceSettingsService.getSettings();
      setMaintenanceTypes(response.data.data.types || []);
    } catch (err) {
      console.error('Error loading maintenance types:', err);
    }
  };

  const handleVehicleClick = (vehicle: Vehicle) => {
    navigate(`/field-agent/vehicles/${vehicle.id}`);
  };

  const filteredVehicles = vehicles.filter(vehicle => {
    const query = searchQuery.toLowerCase();
    
    // Text search
    const matchesSearch = (
      vehicle.registration_number.toLowerCase().includes(query) ||
      vehicle.marque.toLowerCase().includes(query) ||
      vehicle.modele.toLowerCase().includes(query)
    );
    
    // Status filter
    const matchesStatus = !statusFilter || vehicle.status === statusFilter;
    
    // Parking filter
    const matchesParking = !parkingFilter || 
      (parkingFilter === 'assigned' && vehicle.parking_spot) ||
      (parkingFilter === 'unassigned' && !vehicle.parking_spot);
    
    // Maintenance type filter - check if vehicle has maintenance records of specific type
    const matchesMaintenanceType = !maintenanceTypeFilter || 
      (vehicle.maintenance_records && vehicle.maintenance_records.some(record => 
        record.maintenance_type === maintenanceTypeFilter
      ));
    
    // Maintenance status filter - check maintenance records for scheduled/overdue status
    const matchesMaintenanceStatus = !maintenanceStatusFilter || 
      (() => {
        const hasScheduledMaintenance = vehicle.maintenance_records?.some(record => 
          record.status === 'scheduled' || record.status === 'overdue'
        );
        
        if (maintenanceStatusFilter === 'ok') {
          // No scheduled or overdue maintenance
          return !hasScheduledMaintenance && vehicle.status !== 'under_maintenance';
        } else if (maintenanceStatusFilter === 'upcoming') {
          // Has scheduled maintenance
          return vehicle.maintenance_records?.some(record => record.status === 'scheduled') || false;
        } else if (maintenanceStatusFilter === 'overdue') {
          // Has overdue maintenance or is under maintenance
          return vehicle.maintenance_records?.some(record => record.status === 'overdue') || 
                 vehicle.status === 'under_maintenance';
        }
        return true;
      })();
    
    // Date filter - check if vehicle has scheduled maintenance dates within the date range
    const matchesDate = !dateFromFilter && !dateToFilter || 
      (() => {
        if (!dateFromFilter && !dateToFilter) return true;
        
        const checkDate = (dateString: string | undefined) => {
          if (!dateString) return false;
          const date = new Date(dateString);
          const fromDate = dateFromFilter ? new Date(dateFromFilter) : null;
          const toDate = dateToFilter ? new Date(dateToFilter) : null;
          
          if (fromDate && toDate) {
            return date >= fromDate && date <= toDate;
          } else if (fromDate) {
            return date >= fromDate;
          } else if (toDate) {
            return date <= toDate;
          }
          return true;
        };
        
        return checkDate(vehicle.prochaine_revision) || checkDate(vehicle.date_revision);
      })();
    
    return matchesSearch && matchesStatus && matchesParking && matchesMaintenanceType && matchesMaintenanceStatus && matchesDate;
  });

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('');
    setParkingFilter('');
    setMaintenanceTypeFilter('');
    setMaintenanceStatusFilter('');
    setDateFilter('');
    setDateFromFilter('');
    setDateToFilter('');
  };

  const hasActiveFilters = searchQuery || statusFilter || parkingFilter || maintenanceTypeFilter || maintenanceStatusFilter || dateFilter || dateFromFilter || dateToFilter;

  return (
    <Box 
      sx={{ 
        width: '100%',
        minHeight: '100%',
        pt: { xs: 2, sm: 3 },
        px: { xs: 2, sm: 3 },
        pb: { xs: 20, sm: 12 }, // Extra padding to avoid footer overlap on mobile
      }}
    >
      <Container maxWidth="md" disableGutters sx={{ height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Véhicules
        </Typography>
        <Button
          variant="outlined"
          startIcon={<FilterIcon />}
          onClick={() => setShowFilters(!showFilters)}
          size="small"
        >
          Filtres
        </Button>
      </Box>

      <Stack spacing={2} sx={{ mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Rechercher véhicule..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        <Collapse in={showFilters}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Statut</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Statut"
                  MenuProps={getDropdownMenuProps()}
                >
                  <MenuItem value="">Tous les statuts</MenuItem>
                  <MenuItem value="ready">Prêt</MenuItem>
                  <MenuItem value="out">Sorti</MenuItem>
                  <MenuItem value="returned">Rentré</MenuItem>
                  <MenuItem value="under_maintenance">En maintenance</MenuItem>
                  <MenuItem value="washing">En lavage</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Place de parking</InputLabel>
                <Select
                  value={parkingFilter}
                  onChange={(e) => setParkingFilter(e.target.value)}
                  label="Place de parking"
                  MenuProps={getDropdownMenuProps()}
                >
                  <MenuItem value="">Toutes les places</MenuItem>
                  <MenuItem value="assigned">Avec place assignée</MenuItem>
                  <MenuItem value="unassigned">Sans place assignée</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Type de maintenance</InputLabel>
                <Select
                  value={maintenanceTypeFilter}
                  onChange={(e) => setMaintenanceTypeFilter(e.target.value)}
                  label="Type de maintenance"
                  MenuProps={getDropdownMenuProps()}
                >
                  <MenuItem value="">Tous les types</MenuItem>
                  {SYSTEM_MAINTENANCE_TYPES.map((type) => (
                    <MenuItem 
                      key={type.id} 
                      value={type.name}
                      sx={{ 
                        fontWeight: 600,
                        color: 'primary.main',
                        borderLeft: '3px solid',
                        borderLeftColor: 'primary.main',
                        pl: 1.5
                      }}
                    >
                      {type.name}
                    </MenuItem>
                  ))}
                  {maintenanceTypes.length > 0 && (
                    <MenuItem disabled sx={{ opacity: 0.6, fontSize: '0.75rem' }}>
                      ──────────────────
                    </MenuItem>
                  )}
                  {maintenanceTypes.map((type) => (
                    <MenuItem key={type.id} value={type.name}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Statut maintenance</InputLabel>
                <Select
                  value={maintenanceStatusFilter}
                  onChange={(e) => setMaintenanceStatusFilter(e.target.value)}
                  label="Statut maintenance"
                  MenuProps={getDropdownMenuProps()}
                >
                  <MenuItem value="">Tous les statuts</MenuItem>
                  <MenuItem value="ok">À jour</MenuItem>
                  <MenuItem value="upcoming">Bientôt requis</MenuItem>
                  <MenuItem value="overdue">En retard</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Date prévue (du)"
                value={dateFromFilter}
                onChange={(e) => setDateFromFilter(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Date prévue (au)"
                value={dateToFilter}
                onChange={(e) => setDateToFilter(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>

            {hasActiveFilters && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={clearFilters}
                  size="small"
                >
                  Effacer les filtres
                </Button>
              </Box>
            )}
          </Stack>
        </Collapse>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          {hasActiveFilters && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary">
                {filteredVehicles.length} véhicule(s) trouvé(s) sur {vehicles.length} total
                {statusFilter && ` • Statut: ${getStatusLabel(statusFilter as any)}`}
                {parkingFilter === 'assigned' && ' • Avec place assignée'}
                {parkingFilter === 'unassigned' && ' • Sans place assignée'}
                {maintenanceTypeFilter && ` • Type maintenance: ${maintenanceTypeFilter}`}
                {maintenanceStatusFilter && ` • Maintenance: ${maintenanceStatusFilter === 'ok' ? 'À jour' : maintenanceStatusFilter === 'upcoming' ? 'Bientôt requis' : 'En retard'}`}
                {(dateFromFilter || dateToFilter) && ` • Date prévue: ${dateFromFilter ? `du ${new Date(dateFromFilter).toLocaleDateString('fr-FR')}` : ''}${dateFromFilter && dateToFilter ? ' ' : ''}${dateToFilter ? `au ${new Date(dateToFilter).toLocaleDateString('fr-FR')}` : ''}`}
              </Typography>
            </Box>
          )}
          
          <List disablePadding>
            {filteredVehicles.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                {hasActiveFilters ? 'Aucun véhicule ne correspond aux filtres' : 'Aucun véhicule trouvé'}
              </Typography>
          ) : (
            filteredVehicles.map((vehicle) => (
              <Paper 
                key={vehicle.id}
                elevation={1}
                sx={{ 
                  mb: 1, 
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    elevation: 2,
                    bgcolor: 'action.hover'
                  }
                }}
                onClick={() => handleVehicleClick(vehicle)}
              >
                <ListItem>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {vehicle.registration_number}
                        </Typography>
                        <Chip 
                          label={getStatusLabel(vehicle.status)}
                          size="small"
                          sx={{
                            bgcolor: getStatusColor(vehicle.status),
                            color: 'white',
                            fontWeight: 500
                          }}
                        />
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                    secondary={
                      <>
                        <Typography variant="body2" color="text.secondary" component="div">
                          {vehicle.marque} {vehicle.modele} {vehicle.year}
                        </Typography>
                        {vehicle.parking_spot && (
                          <Typography variant="caption" color="text.secondary" component="div">
                            🅿️ {vehicle.parking_spot.spot_number} • 
                            {vehicle.mileage.toLocaleString()} km
                          </Typography>
                        )}
                        {/* Maintenance alerts - separate from vehicle status */}
                        {vehicle.maintenance_records && vehicle.maintenance_records.length > 0 && (
                          <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {vehicle.maintenance_records.map((record, idx) => (
                              <Chip
                                key={idx}
                                label={`${record.maintenance_type} - ${new Date(record.start_date).toLocaleDateString('fr-FR')}`}
                                size="small"
                                color={
                                  record.status === 'overdue' ? 'error' :
                                  record.status === 'scheduled' ? 'info' :
                                  record.status === 'in_progress' ? 'warning' : 'default'
                                }
                                variant="outlined"
                                sx={{ 
                                  fontWeight: 500,
                                  fontSize: '0.7rem',
                                  height: '20px'
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      </>
                    }
                  />
                </ListItem>
              </Paper>
            ))
          )}
        </List>
        </>
      )}
      </Container>
    </Box>
  );
};

export default VehicleList;

